/**
 * 참가자 응답 문항 (TASK-2026-004 SPEC 「포함 범위」 2·3, 중앙 명세 F1-04·F1-05·F1-08).
 *
 * 진행자 관찰 칸을 대체하는 자료다. 그래서 설계 규칙이 몇 가지 있다.
 *
 * - **모르겠다·응답하지 않음은 `null`이다.** `false`로 바꾸지 않는다. "문제 없었다"와
 *   "묻지 못했다"가 같은 값이 되면, 분석이 결측을 통과로 읽는다(F1-08의 unknown 규칙).
 * - **Q1은 플레이 전에 받고 이후 답으로 덮지 않는다.** 플레이하면서 알게 된 것을 처음
 *   이해로 세면 이해도 지표가 부풀려진다(F1-04.3).
 * - **문제 자기 보고는 불편과 치명을 구별한다.** `없었다 / 불편했다 / 진행할 수 없었다`
 *   세 단계다. "진행할 수 없었다"가 F1-08의 치명적 접근성 0건 판정 대상이다.
 * - **자유문장 상한은 서버와 같다.** 문항당 1,000자·전체 6,000자(F1-05). 화면에서 먼저
 *   막아, 다 쓴 답이 413으로 되돌아오는 일이 없게 한다.
 * - **이름·연락처는 받지 않는다.** 눈에 띄는 형태(메일 주소·연속된 긴 숫자)는 저장을
 *   거부하고 지우기를 권한다. 이 검사가 개인정보 제거를 보장한다고 주장하지 않는다.
 */

export type AnswerValue = string | number | boolean | null | readonly string[];

export type Answers = Readonly<Record<string, AnswerValue>>;

/** 문항당 자유문장 상한(자). 서버 `LIMITS.responseFieldChars`와 같다. */
export const MAX_FIELD_CHARS = 1000;

/** 응답 전체 자유문장 상한(자). 서버 `LIMITS.responseTotalChars`와 같다. */
export const MAX_TOTAL_CHARS = 6000;

export const PROBLEM_OPTIONS = ['없었다', '불편했다', '진행할 수 없었다'] as const;

/** "진행할 수 없었다"만 치명 수준이다. 단순 불편과 섞지 않는다. */
export const BLOCKING_PROBLEM = PROBLEM_OPTIONS[2];

export type Phase = 'q1' | 'survey';

export interface Question {
  readonly id: string;
  readonly phase: Phase;
  readonly kind: 'text' | 'scale' | 'choice' | 'multi';
  readonly label: string;
  readonly help?: string;
  readonly options?: readonly string[];
  /** 척도 문항의 양 끝 설명. */
  readonly scale?: { readonly min: 1; readonly max: 5; readonly minLabel: string; readonly maxLabel: string };
}

export const QUESTIONS: readonly Question[] = Object.freeze([
  {
    id: 'q1_open_condition',
    phase: 'q1',
    kind: 'text',
    label: '조각이 열리는 조건을 설명해 주세요.',
    // 정답도 힌트도 보여 주지 않는다(F1-04.3). 모르면 모른다고 답할 수 있다.
    help: '지금 아는 대로 적으면 됩니다. 정답을 맞히는 문항이 아닙니다.',
  },
  {
    id: 'q2_change_order',
    phase: 'survey',
    kind: 'text',
    label: '단계가 올라갈 때 무엇이 어떤 순서로 바뀌었나요?',
    help: '기억나는 순서대로 적어 주세요.',
  },
  {
    id: 'q2_active_attributes',
    phase: 'survey',
    kind: 'multi',
    label: '마지막으로 한 단계에서 맞춰야 했던 것을 모두 고르세요.',
    options: Object.freeze(['색', '문양', '위치', '방향', '그 밖의 것']),
  },
  {
    id: 'q3_retry_intent',
    phase: 'survey',
    kind: 'scale',
    label: '다시 해 보고 싶은 정도를 골라 주세요.',
    scale: { min: 1, max: 5, minLabel: '전혀 아니다', maxLabel: '매우 그렇다' },
  },
  { id: 'q3_retry_reason', phase: 'survey', kind: 'text', label: '그렇게 고른 이유를 적어 주세요.' },
  {
    id: 'min_moves_understanding',
    phase: 'survey',
    kind: 'choice',
    label: '가장 적은 이동 수와 내 이동 수의 차이가 무엇을 뜻하는지 알 수 있었나요?',
    options: Object.freeze(['알 수 있었다', '일부만 알 수 있었다', '알 수 없었다']),
  },
  {
    id: 'reward_satisfaction',
    phase: 'survey',
    kind: 'scale',
    label: '조각을 열었을 때 받은 보상이 만족스러웠나요?',
    scale: { min: 1, max: 5, minLabel: '전혀 아니다', maxLabel: '매우 그렇다' },
  },
  {
    id: 'stop_reason',
    phase: 'survey',
    kind: 'choice',
    label: '체험을 끝낸 이유를 골라 주세요.',
    options: Object.freeze([
      '끝까지 다 했다',
      '조작이나 방향이 어려웠다',
      '규칙을 몰라 막혔다',
      '재미가 없었다',
      '시간이 없었다',
      '화면이나 기기 문제가 있었다',
      '그 밖의 이유',
    ]),
  },
  { id: 'stop_reason_detail', phase: 'survey', kind: 'text', label: '이유를 조금 더 적어 주실 수 있나요?' },
  {
    id: 'color_problem',
    phase: 'survey',
    kind: 'choice',
    label: '색을 구별하기 어려운 문제가 있었나요?',
    options: PROBLEM_OPTIONS,
  },
  {
    id: 'motion_problem',
    phase: 'survey',
    kind: 'choice',
    label: '움직임·회전 때문에 불편하거나 어지러운 문제가 있었나요?',
    options: PROBLEM_OPTIONS,
  },
  {
    id: 'touch_problem',
    phase: 'survey',
    kind: 'choice',
    label: '터치나 조작이 잘 되지 않는 문제가 있었나요?',
    options: PROBLEM_OPTIONS,
  },
  {
    id: 'accessibility_detail',
    phase: 'survey',
    kind: 'text',
    label: '위 문제를 겪었다면 어떤 상황이었는지 적어 주세요.',
  },
]);

export const Q1_ID = 'q1_open_condition';

/** 안전 판정에 쓰는 문항. 결측이면 "문제 없음"으로 통과시키지 않는다(F1-08). */
export const SAFETY_IDS: readonly string[] = Object.freeze(['color_problem', 'motion_problem', 'touch_problem']);

export function questionsOf(phase: Phase): readonly Question[] {
  return QUESTIONS.filter((question) => question.phase === phase);
}

/** 모든 문항을 "응답하지 않음"으로 둔 초기값. */
export function emptyAnswers(): Record<string, AnswerValue> {
  const answers: Record<string, AnswerValue> = {};
  for (const question of QUESTIONS) {
    answers[question.id] = null;
  }
  return answers;
}

export function isAnswered(value: AnswerValue | undefined): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === 'string') {
    return value.trim() !== '';
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return true;
}

/** 글자 수는 서버와 같은 기준(코드 포인트)으로 센다. */
export function charCount(value: AnswerValue | undefined): number {
  if (typeof value === 'string') {
    return [...value].length;
  }
  if (Array.isArray(value)) {
    return value.reduce((sum: number, item) => sum + [...String(item)].length, 0);
  }
  return 0;
}

export function totalChars(answers: Answers): number {
  return Object.values(answers).reduce((sum: number, value) => sum + charCount(value), 0);
}

const EMAIL_LIKE = /[^\s@]+@[^\s@]+\.[^\s@]+/;
/** 구분 기호를 뺀 뒤 9자리 이상 이어지는 숫자. 전화번호·주민번호 모양이다. */
const LONG_DIGITS = /\d{9,}/;

/**
 * 연락처처럼 보이는 내용이 있는가.
 *
 * **보장이 아니다.** 이 검사를 통과했다고 개인정보가 없다는 뜻이 아니며(F1-05),
 * 참가자에게 적지 말라고 안내하는 것이 1차 방어다. 여기서는 눈에 띄는 형태만
 * 잡아 저장을 멈추고 지우기를 권한다.
 */
export function looksLikeContact(value: AnswerValue | undefined): boolean {
  if (typeof value !== 'string') {
    return false;
  }
  const compact = value.replace(/[\s.\-()]/g, '');
  return EMAIL_LIKE.test(value) || LONG_DIGITS.test(compact);
}

export interface AnswerIssue {
  readonly id: string;
  readonly code: 'too_long' | 'total_too_long' | 'contact_like' | 'unknown_option' | 'out_of_range';
}

/** 저장 전 검사. 위반이 있으면 그 문항을 고칠 수 있게 사유와 함께 돌려준다. */
export function checkAnswers(answers: Answers): readonly AnswerIssue[] {
  const issues: AnswerIssue[] = [];
  for (const question of QUESTIONS) {
    const value = answers[question.id];
    if (!isAnswered(value)) {
      continue;
    }
    if (question.kind === 'text') {
      if (charCount(value) > MAX_FIELD_CHARS) {
        issues.push({ id: question.id, code: 'too_long' });
      }
      if (looksLikeContact(value)) {
        issues.push({ id: question.id, code: 'contact_like' });
      }
      continue;
    }
    if (question.kind === 'scale') {
      const ok = typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 5;
      if (!ok) {
        issues.push({ id: question.id, code: 'out_of_range' });
      }
      continue;
    }
    const options = question.options ?? [];
    if (question.kind === 'choice') {
      if (typeof value !== 'string' || !options.includes(value)) {
        issues.push({ id: question.id, code: 'unknown_option' });
      }
      continue;
    }
    if (!Array.isArray(value) || !value.every((item) => typeof item === 'string' && options.includes(item))) {
      issues.push({ id: question.id, code: 'unknown_option' });
    }
  }
  if (totalChars(answers) > MAX_TOTAL_CHARS) {
    issues.push({ id: '', code: 'total_too_long' });
  }
  return issues;
}

/**
 * 서버로 보낼 값만 남긴다.
 *
 * 문항 목록에 없는 키는 보내지 않는다. 화면 상태가 응답에 섞여 들어가는 경로를 막는다.
 * 빈 문자열은 `null`로 보낸다 — 빈 답과 응답하지 않음을 서버에서 구별할 이유가 없고,
 * `false`로 바꾸는 일은 어디에서도 하지 않는다.
 */
export function toPayload(answers: Answers): Record<string, AnswerValue> {
  const payload: Record<string, AnswerValue> = {};
  for (const question of QUESTIONS) {
    const value = answers[question.id];
    if (!isAnswered(value)) {
      payload[question.id] = null;
      continue;
    }
    payload[question.id] = typeof value === 'string' ? value.trim() : (value ?? null);
  }
  return payload;
}

/** 응답하지 않은 문항 수. 화면이 "몇 개가 비어 있다"를 사실대로 말하는 데 쓴다. */
export function unansweredIds(answers: Answers, phase?: Phase): readonly string[] {
  return QUESTIONS.filter((question) => (phase === undefined || question.phase === phase) && !isAnswered(answers[question.id])).map(
    (question) => question.id,
  );
}
